import React from "react";
import { Link } from "react-router-dom";
import {
  SYSTEM_MESSAGE_FALLBACK,
  normalizeSystemData,
} from "./systemMessageText";
import { getPublicProfileHref } from "../../utils/publicProfile";

/**
 * Participant name rendering for system messages.
 *
 * Canonical path: when `handle` or `public_id` resolves via getPublicProfileHref,
 * render a React Router Link with scoped visual class.
 *
 * Legacy/malformed fallback: participants without a canonical public identifier
 * remain plain text (span). Never invent `/user/<internal-numeric-id>` URLs.
 * Current backend `_participant_payload` always emits public_id (+ handle when set).
 */
function ParticipantLink({ participant }) {
  if (!participant) return null;
  const href = getPublicProfileHref(participant);
  if (!href) {
    return <span className="system-message-participant-text">{participant.fullname}</span>;
  }
  return (
    <Link to={href} className="system-message-participant-link">
      {participant.fullname}
    </Link>
  );
}

export default function SystemMessage({ message }) {
  const data = normalizeSystemData(message?.system_data);

  const renderText = () => {
    if (!data) {
      return <div className="text">{SYSTEM_MESSAGE_FALLBACK}</div>;
    }

    const { action, actor, target, new_name: newName } = data;
    switch (action) {
      case "promote":
        return (
          <div className="text">
            <ParticipantLink participant={actor} />
            {" підвищив "}
            <ParticipantLink participant={target} />
            {" до адміністратора"}
          </div>
        );
      case "demote":
        return (
          <div className="text">
            <ParticipantLink participant={actor} />
            {" понизив "}
            <ParticipantLink participant={target} />
            {" до учасника"}
          </div>
        );
      case "add":
        return (
          <div className="text">
            <ParticipantLink participant={actor} />
            {" додав "}
            <ParticipantLink participant={target} />
          </div>
        );
      case "remove":
        return (
          <div className="text">
            <ParticipantLink participant={actor} />
            {" видалив "}
            <ParticipantLink participant={target} />
          </div>
        );
      case "leave":
        return (
          <div className="text">
            <ParticipantLink participant={actor} />
            {" вийшов з чату"}
          </div>
        );
      case "transfer_owner":
        return (
          <div className="text">
            <ParticipantLink participant={actor} />
            {" передав права власника "}
            <ParticipantLink participant={target} />
          </div>
        );
      case "rename_chat":
        return (
          <div className="text">
            <ParticipantLink participant={actor} />
            {" змінив назву чату"}
            {newName ? ` на "${newName}"` : ""}
          </div>
        );
      case "update_chat_image":
        return (
          <div className="text">
            <ParticipantLink participant={actor} />
            {" оновив фото чату"}
          </div>
        );
      default:
        return <div className="text">{SYSTEM_MESSAGE_FALLBACK}</div>;
    }
  };

  return (
    <div className="system-message">
      {renderText()}
    </div>
  );
}
